import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/modules/Auth/store/index'
import { APPLICANT_LANDING_PATH } from '@/modules/Core/constants/adminAccess'
import {
  getAuthenticatedLandingPath,
  getRoutePermissionMode,
  getRouteRequiredPermissions,
} from '@/modules/Core/utils/authorization'
import { canAccessPermissions } from '@/modules/Core/utils/permissions'

function isSamePath(targetPath: string, to: RouteLocationNormalized) {
  return targetPath === to.path || targetPath === to.fullPath
}

function redirectToFirstAccessible(
  to: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>,
  next: NavigationGuardNext
) {
  const path = getAuthenticatedLandingPath(
    authStore.roles,
    authStore.permissions
  )

  if (to.name === 'login') {
    if (path) {
      next(path)
      return
    }

    authStore.logout()
    next()
    return
  }

  if (path && !isSamePath(path, to)) {
    next(path)
    return
  }

  if (path && isSamePath(path, to)) {
    next()
    return
  }

  authStore.logout()
  next({ name: 'login' })
}

export function authGuard(
  to: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore()

  if (to.name !== 'login' && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    redirectToFirstAccessible(to, authStore, next)
    return
  }

  if (!authStore.isAuthenticated) {
    next()
    return
  }

  const requiredPermissions = getRouteRequiredPermissions(to.matched)

  if (
    requiredPermissions &&
    !canAccessPermissions(
      authStore.permissions,
      requiredPermissions,
      getRoutePermissionMode(to.matched)
    )
  ) {
    redirectToFirstAccessible(to, authStore, next)
    return
  }

  if (authStore.isApplicant && to.name === 'home') {
    next(APPLICANT_LANDING_PATH)
    return
  }

  next()
}
